import { CircleAlert, Lock, TriangleAlert } from 'lucide-react';

import { ApiError } from '@/lib/api/client';

import { StateBlock } from './StateBlock';

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  /** What the operator was trying to load, e.g. "leagues" or "this roster". */
  subject?: string;
}

function RetryButton({ onRetry }: { onRetry: () => void }) {
  return (
    <button
      type="button"
      onClick={onRetry}
      className="border-bd-s bg-elev text-tx hover:bg-high rounded-md border px-3 py-1.5 text-[13px] font-medium"
    >
      Try again
    </button>
  );
}

/**
 * Error state for a failed read. Distinguishes permission-denied from a genuine
 * failure, because "you cannot see this" and "this is broken" call for entirely
 * different operator responses.
 */
export function ErrorState({ error, onRetry, subject = 'this view' }: ErrorStateProps) {
  if (error instanceof ApiError && error.isPermissionDenied) {
    return <PermissionDenied />;
  }

  const isNetwork = error instanceof ApiError && error.status === 0;
  const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
  const requestDetail =
    error instanceof ApiError && error.code ? `code: ${error.code}` : undefined;

  return (
    <StateBlock
      icon={isNetwork ? CircleAlert : TriangleAlert}
      tone="danger"
      title={isNetwork ? 'Cannot reach the API' : `Could not load ${subject}`}
      description={message}
      detail={requestDetail}
      actions={onRetry ? <RetryButton onRetry={onRetry} /> : undefined}
    />
  );
}

export function PermissionDenied({ capability }: { capability?: string }) {
  return (
    <StateBlock
      icon={Lock}
      tone="warning"
      title="Not permitted"
      description="Your role does not include access to this area. Ask an owner to grant it in Settings & Access if you need it."
      detail={capability ? `requires: ${capability}` : undefined}
    />
  );
}
